/* ==========================================================================
   GEO ROCKS THE 'TISM — Product page behaviour
   --------------------------------------------------------------------------
   Self-contained custom elements. No third-party dependencies, no globals
   other than the element registrations below. Nothing here touches the
   theme's product form, variant picker, media gallery or cart logic.

   Registers:
     <geo-tabs>        tabbed panels (Product details / Size guide / …)
     <geo-size-table>  sub-tabbed measurement tables
     <geo-modal>       accessible dialog used by the size guide
     <geo-reviews>     scroll-snap review carousel
   ========================================================================== */
(function () {
  'use strict';

  var REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

  var FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  function scrollBehavior() {
    return REDUCED_MOTION.matches ? 'auto' : 'smooth';
  }

  /* ------------------------------------------------------------------
     <geo-tabs>
     ------------------------------------------------------------------ */
  if (!customElements.get('geo-tabs')) {
    customElements.define(
      'geo-tabs',
      class GeoTabs extends HTMLElement {
        connectedCallback() {
          this.tabs = Array.prototype.slice.call(
            this.querySelectorAll('[role="tab"]')
          );
          this.panels = Array.prototype.slice.call(
            this.querySelectorAll('[role="tabpanel"]')
          );

          if (!this.tabs.length) return;

          this.onTabClick = this.onTabClick.bind(this);
          this.onKeydown = this.onKeydown.bind(this);

          this.tabs.forEach(function (tab) {
            tab.addEventListener('click', this.onTabClick);
            tab.addEventListener('keydown', this.onKeydown);
          }, this);

          this.selectFromHash();
        }

        disconnectedCallback() {
          this.tabs.forEach(function (tab) {
            tab.removeEventListener('click', this.onTabClick);
            tab.removeEventListener('keydown', this.onKeydown);
          }, this);
        }

        selectFromHash() {
          var hash = window.location.hash.replace('#', '');
          if (!hash) return;
          var match = this.tabs.filter(function (tab) {
            return tab.dataset.tabId === hash;
          })[0];
          if (match) this.select(match, false);
        }

        onTabClick(event) {
          this.select(event.currentTarget);
        }

        onKeydown(event) {
          var index = this.tabs.indexOf(event.currentTarget);
          if (index === -1) return;

          var next = null;
          switch (event.key) {
            case 'ArrowRight':
            case 'ArrowDown':
              next = this.tabs[(index + 1) % this.tabs.length];
              break;
            case 'ArrowLeft':
            case 'ArrowUp':
              next = this.tabs[(index - 1 + this.tabs.length) % this.tabs.length];
              break;
            case 'Home':
              next = this.tabs[0];
              break;
            case 'End':
              next = this.tabs[this.tabs.length - 1];
              break;
            default:
              return;
          }

          event.preventDefault();
          this.select(next);
          next.focus();
        }

        /**
         * @param {HTMLElement} tab   The tab button to activate.
         * @param {boolean}     focus Whether to scroll the tab into view.
         */
        select(tab, focus) {
          if (!tab) return;

          this.tabs.forEach(function (item) {
            var selected = item === tab;
            item.setAttribute('aria-selected', selected ? 'true' : 'false');
            item.setAttribute('tabindex', selected ? '0' : '-1');
          });

          this.panels.forEach(function (panel) {
            panel.hidden = panel.getAttribute('aria-labelledby') !== tab.id;
          });

          if (focus !== false && tab.scrollIntoView) {
            tab.scrollIntoView({
              behavior: scrollBehavior(),
              block: 'nearest',
              inline: 'nearest'
            });
          }
        }

        /**
         * Public API — activate a panel by its data-tab-id.
         * @param {string} id
         */
        selectById(id) {
          var match = this.tabs.filter(function (tab) {
            return tab.dataset.tabId === id;
          })[0];
          if (match) this.select(match);
          return !!match;
        }
      }
    );
  }

  /* ------------------------------------------------------------------
     <geo-size-table>
     ------------------------------------------------------------------ */
  if (!customElements.get('geo-size-table')) {
    customElements.define(
      'geo-size-table',
      class GeoSizeTable extends HTMLElement {
        connectedCallback() {
          this.buttons = Array.prototype.slice.call(
            this.querySelectorAll('[data-size-tab]')
          );
          this.panels = Array.prototype.slice.call(
            this.querySelectorAll('[data-size-panel]')
          );

          if (this.buttons.length < 2) return;

          this.onClick = this.onClick.bind(this);
          this.buttons.forEach(function (button) {
            button.addEventListener('click', this.onClick);
          }, this);
        }

        disconnectedCallback() {
          this.buttons.forEach(function (button) {
            button.removeEventListener('click', this.onClick);
          }, this);
        }

        onClick(event) {
          var key = event.currentTarget.dataset.sizeTab;

          this.buttons.forEach(function (button) {
            button.setAttribute(
              'aria-selected',
              button.dataset.sizeTab === key ? 'true' : 'false'
            );
          });

          this.panels.forEach(function (panel) {
            panel.hidden = panel.dataset.sizePanel !== key;
          });
        }
      }
    );
  }

  /* ------------------------------------------------------------------
     <geo-modal>
     ------------------------------------------------------------------ */
  if (!customElements.get('geo-modal')) {
    customElements.define(
      'geo-modal',
      class GeoModal extends HTMLElement {
        connectedCallback() {
          this.dialog = this.querySelector('[data-modal-dialog]');
          this.opener = null;

          this.onKeydown = this.onKeydown.bind(this);
          this.close = this.close.bind(this);

          Array.prototype.forEach.call(
            this.querySelectorAll('[data-modal-close]'),
            function (button) {
              button.addEventListener('click', this.close);
            },
            this
          );
        }

        disconnectedCallback() {
          document.removeEventListener('keydown', this.onKeydown);
          this.releaseScroll();
        }

        open(opener) {
          this.opener = opener || null;
          this.classList.add('is-open');
          this.setAttribute('aria-hidden', 'false');
          document.addEventListener('keydown', this.onKeydown);
          this.lockScroll();

          var target =
            this.dialog && this.dialog.querySelector(FOCUSABLE);
          if (target) target.focus();
        }

        close() {
          this.classList.remove('is-open');
          this.setAttribute('aria-hidden', 'true');
          document.removeEventListener('keydown', this.onKeydown);
          this.releaseScroll();

          if (this.opener && document.contains(this.opener)) {
            this.opener.focus();
          }
          this.opener = null;
        }

        lockScroll() {
          this.previousOverflow = document.body.style.overflow;
          document.body.style.overflow = 'hidden';
        }

        releaseScroll() {
          if (typeof this.previousOverflow === 'string') {
            document.body.style.overflow = this.previousOverflow;
            this.previousOverflow = undefined;
          }
        }

        onKeydown(event) {
          if (event.key === 'Escape') {
            event.preventDefault();
            this.close();
            return;
          }

          if (event.key !== 'Tab' || !this.dialog) return;

          var nodes = Array.prototype.filter.call(
            this.dialog.querySelectorAll(FOCUSABLE),
            function (node) {
              return node.offsetParent !== null;
            }
          );
          if (!nodes.length) return;

          var first = nodes[0];
          var last = nodes[nodes.length - 1];

          if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
          }
        }
      }
    );
  }

  /* ------------------------------------------------------------------
     <geo-reviews>
     ------------------------------------------------------------------ */
  if (!customElements.get('geo-reviews')) {
    customElements.define(
      'geo-reviews',
      class GeoReviews extends HTMLElement {
        connectedCallback() {
          this.track = this.querySelector('[data-reviews-track]');
          this.prevButton = this.querySelector('[data-reviews-prev]');
          this.nextButton = this.querySelector('[data-reviews-next]');
          this.dots = Array.prototype.slice.call(
            this.querySelectorAll('[data-reviews-dot]')
          );

          if (!this.track) return;

          this.onScroll = this.throttle(this.update.bind(this), 100);
          this.track.addEventListener('scroll', this.onScroll, {
            passive: true
          });

          if (this.prevButton) {
            this.prevButton.addEventListener('click', this.step.bind(this, -1));
          }
          if (this.nextButton) {
            this.nextButton.addEventListener('click', this.step.bind(this, 1));
          }

          this.dots.forEach(function (dot, index) {
            dot.addEventListener('click', this.goTo.bind(this, index));
          }, this);

          this.resizeObserver =
            'ResizeObserver' in window
              ? new ResizeObserver(this.onScroll)
              : null;
          if (this.resizeObserver) this.resizeObserver.observe(this.track);

          this.update();
        }

        disconnectedCallback() {
          if (this.track) this.track.removeEventListener('scroll', this.onScroll);
          if (this.resizeObserver) this.resizeObserver.disconnect();
        }

        get slides() {
          return Array.prototype.slice.call(this.track.children);
        }

        slideStep() {
          var slides = this.slides;
          if (slides.length < 2) return this.track.clientWidth;
          return Math.round(slides[1].offsetLeft - slides[0].offsetLeft);
        }

        step(direction) {
          var amount = this.slideStep() * direction;
          this.track.scrollBy({ left: amount, behavior: scrollBehavior() });
        }

        goTo(index) {
          var slides = this.slides;
          if (!slides[index]) return;
          this.track.scrollTo({
            left: slides[index].offsetLeft - slides[0].offsetLeft,
            behavior: scrollBehavior()
          });
        }

        update() {
          if (!this.track) return;

          var maxScroll = this.track.scrollWidth - this.track.clientWidth;
          var position = this.track.scrollLeft;

          if (this.prevButton) {
            this.prevButton.disabled = position <= 2;
          }
          if (this.nextButton) {
            this.nextButton.disabled = position >= maxScroll - 2;
          }

          if (!this.dots.length) return;

          var step = this.slideStep() || 1;
          var active = Math.round(position / step);

          this.dots.forEach(function (dot, index) {
            dot.setAttribute('aria-current', index === active ? 'true' : 'false');
          });
        }

        throttle(fn, wait) {
          var frame = null;
          var last = 0;
          return function () {
            var now = Date.now();
            if (now - last >= wait) {
              last = now;
              fn();
            } else if (!frame) {
              frame = window.setTimeout(function () {
                frame = null;
                last = Date.now();
                fn();
              }, wait);
            }
          };
        }
      }
    );
  }

  /* ------------------------------------------------------------------
     Size-guide triggers
     ------------------------------------------------------------------
     The theme ships a `[data-open-sizeguide]` button next to the Size
     option, hidden by default because it is owned by the FoxKit app.
     When a Geo size-guide modal exists on the page we reveal those
     buttons and bind them, without removing FoxKit's own handling if the
     app is installed (we simply never run when no modal is present).
     ------------------------------------------------------------------ */
  function getSizeGuideModal() {
    return document.querySelector('geo-modal[data-size-guide]');
  }

  function bindSizeGuideTriggers(root) {
    var modal = getSizeGuideModal();
    if (!modal) return;

    var scope = root || document;
    var triggers = scope.querySelectorAll(
      '[data-open-sizeguide]:not([data-geo-bound])'
    );

    Array.prototype.forEach.call(triggers, function (trigger) {
      trigger.setAttribute('data-geo-bound', 'true');
      trigger.classList.remove('m:hidden');
      trigger.setAttribute('aria-haspopup', 'dialog');

      trigger.addEventListener('click', function (event) {
        event.preventDefault();
        var target = getSizeGuideModal();
        if (target && typeof target.open === 'function') target.open(trigger);
      });
    });
  }

  function init() {
    bindSizeGuideTriggers(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* The theme re-renders the variant picker on variant change, which
     recreates the size-guide button. Re-bind whenever that happens. */
  document.addEventListener('variant:changed', function () {
    window.setTimeout(function () {
      bindSizeGuideTriggers(document);
    }, 0);
  });

  /* Shopify theme editor: re-bind after a section is re-rendered. */
  document.addEventListener('shopify:section:load', function (event) {
    bindSizeGuideTriggers(event.target);
  });
})();
